'use strict';
import get from 'lodash/get';
import {createEventHandler} from 'recompose';
import 'rxjs/add/observable/of';
import 'rxjs/add/operator/combineLatest';
import 'rxjs/add/operator/last';
import 'rxjs/add/operator/takeUntil';
import {getItemFromState} from '../../../_libs/redux-observable-list';
import {animatedStateGo, stateGoBack} from '../../../app/actions';
import {ViewSwitcherDirection} from '../../../app/enums';
import {getAssetProjectReduxItem, getProjectModel, getProjectReduxItem} from '../../../app/model/reducers/state';
import {buildBreadcrumbPath$} from '../../../app/navigation/utils';
import {navStates} from '../../../app/router';
import {supportsDownload} from '../../../core-utilities-implementation/device-services/DeviceServicesCordova';
import {getId} from '../../../core-utilities/domain/ReduxUtils';
import {buildFilterData$} from '../../../core-utilities/redux/ReduxListContainer';
import {TaskType} from '../../../tasks/enums/TaskType';
import {updateMilestoneIdFilter} from '../../../tasks/redux/utils';


/**
 * @param {Object} config
 * @param {TaskType} config.taskType
 * @param {ReduxList} config.reduxList
 * @param {String} config.navBackState Go back to this Navigation view.
 * @param {ReduxRequestModal} [config.updateReduxRequestModal] Required for taskTye === PROJECT
 */
export const Container = (config) => (state$, dispatch) => (props$) => {
  const {
    taskType,
    taskDetailsState,
    reduxList,
    updateReduxRequestModal,
    navBackState,
  } = config;

  /** @type {ReduxObservableListActions} */
  const actions = reduxList.actions;
  /** @type {ReduxObservableListOptions} */
  const reduxListOptions = reduxList.options;

  const filterData$ = buildFilterData$(state$, reduxListOptions);

  const {
    handler: onGoBack,
    stream: back$,
  } = createEventHandler();
  // need actions for milestone toggles
  const {
    handler: onToggleMilestoneVisibility,
    stream: toggleMilestoneVisibility$,
  } = createEventHandler();

  // TODO: only show edit button if taskTye === PROJECT
  // TODO: hide button if user does not have project edit permission
  const onClickEdit = taskType === TaskType.PROJECT
    ? createEditEvent(updateReduxRequestModal, state$, dispatch, props$)
    : null;
  const onClickVisibilityButton = (e, model) => onToggleMilestoneVisibility(getId(model));
  const onClickVisibilityToggleAllButton = () => onToggleMilestoneVisibility('all');
  const onTaskClick = (e, model) => dispatch(animatedStateGo({
    state: taskDetailsState,
    params: {taskId: getId(model)},
    direction: ViewSwitcherDirection.forward,
  }));

  const projectItem$ = state$
    .map(taskType === TaskType.ASSET ? getAssetProjectReduxItem : getProjectReduxItem)
    .distinctUntilChanged();

  back$
    .withLatestFrom(projectItem$)
    .debounceTime(300)
    .takeUntil(props$.last())
    .subscribe(([ev, projectItem]) => {
      // Make sure a project is loaded before going back.
      if (getItemFromState(projectItem)) {
        dispatch(stateGoBack({state: navBackState}));
      } else {
        dispatch(stateGoBack({state: navStates.orgsList}));
      }
    });

  const isVisibilityAllCollapsed$ = filterData$
    .map(filterData => get(filterData.filterOptions, 'milestone.id.all', false))
    .distinctUntilChanged();

  toggleMilestoneVisibility$
    .withLatestFrom(projectItem$, filterData$)
    .takeUntil(props$.last())
    .subscribe(([id, projectItem, filterData]) => {
      const filterOptions = updateMilestoneIdFilter(id, filterData);
      dispatch(actions.applyFilter({...filterData, filterOptions}, {parentId: projectItem.id}));
    });

  const breadcrumbPath$ = buildBreadcrumbPath$({state$, taskType});

  // TODO: auto-nav away if the model state for this view does not exist (is deleted, etc)

  return props$
    .combineLatest(
      breadcrumbPath$,
      projectItem$,
      isVisibilityAllCollapsed$,
      (props, breadcrumbPath, projectItem, isVisibilityAllCollapsed) => ({
        ...props,
        inProject: taskType === TaskType.ASSET ? !!getItemFromState(projectItem) : true,
        project: getItemFromState(projectItem),
        taskType,
        breadcrumbPath,
        parentId: projectItem.id,
        init: projectItem.init,
        modelMode: projectItem.mode,
        isVisibilityAllCollapsed,

        supportsDownload: supportsDownload(),
        onGoBack,
        onClickEdit,
        onClickVisibilityButton,
        onClickVisibilityToggleAllButton,
        onTaskClick,
      }));
};

/**
 * @param {ReduxRequestModal} updateReduxRequestModal
 * @param {Observable} state$
 * @param {Function} dispatch
 * @param {Observable} props$
 */
function createEditEvent(updateReduxRequestModal, state$, dispatch, props$) {
  const {
    handler: onClickEdit,
    stream: edit$,
  } = createEventHandler();

  edit$
    .withLatestFrom(state$)
    .takeUntil(props$.last())
    .subscribe(([e, rootState]) => {
      e.stopPropagation();
      const project = getProjectModel(rootState);
      dispatch(updateReduxRequestModal.actions.showModal({model: project}));
    });

  return onClickEdit;
}



// WEBPACK FOOTER //
// ./src/js/projects/components/ProjectViewTasks/Container.js